import { Grid } from "@mui/material";
import { useAffiliateEarningsQuery } from "Api/Queries/Lead/useAffiliateEarningsQuery";
import { useLoyaltiesOverviewQuery } from "Api/Queries/Loyalty/useLoyaltiesOverviewQuery";
import { formatCurrency } from "Utils/CurrencyUtils";
import { PayoutTile } from "Components/Affiliate/Earnings/PayoutTile";
import { TotalEarnedTile } from "Components/Affiliate/Earnings/TotalEarnedTile";

type Props = {
  payoutHint: string;
  totalEarnedHint: string;
};

type CombineValueArgs = {
  affiliateAmount: number;
  loyaltyAmount: number;
  affiliateCurrency?: string;
  loyaltyCurrency?: string;
};

// Both streams are market-scoped, so the currencies normally match. Amounts must
// never be summed across currencies, so a mismatch renders the streams side by side.
export const combineEarningsValue = ({
  affiliateAmount,
  loyaltyAmount,
  affiliateCurrency,
  loyaltyCurrency,
}: CombineValueArgs) => {
  const knownCurrency = affiliateCurrency ?? loyaltyCurrency;
  const hasCurrencyConflict =
    !!affiliateCurrency &&
    !!loyaltyCurrency &&
    affiliateCurrency !== loyaltyCurrency;

  if (!hasCurrencyConflict) {
    // An unknown currency means that stream has no earnings, so summing
    // under the one known currency is safe.
    return formatCurrency({
      amount: affiliateAmount + loyaltyAmount,
      currencyCode: knownCurrency,
    });
  }

  if (affiliateAmount === 0) {
    return formatCurrency({
      amount: loyaltyAmount,
      currencyCode: loyaltyCurrency,
    });
  }

  if (loyaltyAmount === 0) {
    return formatCurrency({
      amount: affiliateAmount,
      currencyCode: affiliateCurrency,
    });
  }

  return `${formatCurrency({ amount: affiliateAmount, currencyCode: affiliateCurrency })} + ${formatCurrency({ amount: loyaltyAmount, currencyCode: loyaltyCurrency })}`;
};

export const CombinedEarningsSummary = ({
  payoutHint,
  totalEarnedHint,
}: Props) => {
  const { data: affiliateData, isLoading: isAffiliateLoading } =
    useAffiliateEarningsQuery();
  const { data: loyaltyData, isLoading: isLoyaltyLoading } =
    useLoyaltiesOverviewQuery();

  const isLoading = isAffiliateLoading || isLoyaltyLoading;

  if (!isLoading && !affiliateData && !loyaltyData) {
    return null;
  }

  const affiliatePayout = affiliateData?.currentPayoutAmount ?? 0;
  const affiliateTotal = affiliateData?.totalEarned ?? 0;
  const loyaltyPayout = loyaltyData?.toPayout?.value ?? 0;
  const loyaltyTotal = loyaltyData?.totalEarned?.value ?? 0;

  // Affiliate returns an empty-string currency for a region-less party — treat it as unknown.
  const affiliateCurrency = affiliateData?.currentPayoutCurrency || undefined;
  const loyaltyCurrency = loyaltyData?.toPayout?.currencyCode;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <PayoutTile
          isLoading={isLoading}
          value={combineEarningsValue({
            affiliateAmount: affiliatePayout,
            loyaltyAmount: loyaltyPayout,
            affiliateCurrency,
            loyaltyCurrency,
          })}
          hint={payoutHint}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TotalEarnedTile
          isLoading={isLoading}
          value={combineEarningsValue({
            affiliateAmount: affiliateTotal,
            loyaltyAmount: loyaltyTotal,
            affiliateCurrency,
            loyaltyCurrency,
          })}
          hint={totalEarnedHint}
        />
      </Grid>
    </Grid>
  );
};
